/**
 * PaymentsTable — the one payments table, shared by every surface that lists the
 * payments correlated with something: the artifact detail page, the execution
 * detail page, and anywhere else that needs "the payments for these ids".
 *
 * It renders the same rich table the bulk payment search uses — the shared
 * payment columns (incl. the per-ref-type dynamic columns), a status/state
 * PropertyFilter, server-side pagination, a page-size + visible-column
 * preferences control, and a Refresh — backed by the paginated SearchPayments
 * API, region-pinned and scoped by paymentIdentifiers = `ids`.
 *
 * It owns its own page / page size / filter / visible-column state (kept out of
 * the bulk-search Redux so these surfaces never share a page or filter).
 */
import { useEffect, useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import CollectionPreferences from '@cloudscape-design/components/collection-preferences';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import PropertyFilter, { PropertyFilterProps } from '@cloudscape-design/components/property-filter';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { searchPayments, clearSearchPayments } from 'src/store/slices/searchPayments/searchPaymentsSlice';
import { PaymentFilters, SearchPaymentsInput } from 'src/store/slices/searchPayments/searchPaymentsApi';
import { paymentSearchDescriptor } from '../bulkSearch/paymentSearchDescriptor';
import { firstOf, queryToFilterMap } from '../bulkSearch/bulkSearchShared';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250, 500, 1000].map((v) => ({ value: v, label: String(v) }));
const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_VISIBLE = paymentSearchDescriptor.defaultVisibleColumns
  ?? paymentSearchDescriptor.columns.map((c) => c.id!).filter(Boolean);

/** Build the SearchPayments input for the given ids at the requested page/filters. */
const buildInput = (
  ids: string[], pageNumber: number, pageSize: number, filters: Record<string, string[]>,
): SearchPaymentsInput => {
  const f: PaymentFilters = { paymentIdentifiers: ids };
  const status = firstOf(filters, 'status');
  const state = firstOf(filters, 'state');
  if (status) f.beaconPaymentStatus = status;
  if (state) f.beaconPaymentState = state;
  return { filters: f, pagination: { pageNumber, pageSize } };
};

export const PaymentsTable = (
  { ids, region }: { ids: string[]; region: string | null },
) => {
  const dispatch = useAppDispatch();
  const payments = useAppSelector((s) => s.searchPayments.payments);
  const total = useAppSelector((s) => s.searchPayments.total);
  const loading = useAppSelector((s) => s.searchPayments.loading);
  const error = useAppSelector((s) => s.searchPayments.error);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filterTokens, setFilterTokens] = useState<readonly PropertyFilterProps.Token[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE);
  // True only while a refresh this table triggered is in flight, so paging does
  // not grey out the Refresh button.
  const [refreshing, setRefreshing] = useState(false);

  // Stable key for the id list so a new array literal from the caller does not
  // thrash the fetch / reset effects.
  const idsKey = ids.join('\u0001');
  const query = useMemo<PropertyFilterProps.Query>(
    () => ({ operation: 'and', tokens: [...filterTokens] }), [filterTokens],
  );
  const filters = useMemo(() => queryToFilterMap(query), [query]);

  // Back to page one whenever the id set (or its region) changes.
  useEffect(() => { setPageNumber(1); setFilterTokens([]); }, [idsKey, region]);

  // Fetch this page, region-pinned and server-paginated. A superseded request is
  // aborted so a slow page can never overwrite a newer one.
  useEffect(() => {
    if (ids.length === 0 || !region) return undefined;
    const promise = dispatch(searchPayments({
      input: buildInput(ids, pageNumber, pageSize, filters), region,
    }));
    return () => { promise.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, region, pageNumber, pageSize, filters, dispatch]);

  // Drop the slice when the table goes away so a return visit starts clean.
  useEffect(() => () => { dispatch(clearSearchPayments()); }, [dispatch]);

  // Clear the refresh-in-flight flag once the fetch settles.
  useEffect(() => { if (!loading) setRefreshing(false); }, [loading]);

  const dynamicColumns = useMemo(
    () => paymentSearchDescriptor.dynamicColumns?.(payments) ?? [], [payments],
  );
  const allColumns = useMemo(
    () => [...paymentSearchDescriptor.columns, ...dynamicColumns], [dynamicColumns],
  );
  const activeColumns = allColumns.filter((c) => c.id && visibleColumns.includes(c.id));
  const visibleContentOptions = allColumns.map((c) => ({
    id: c.id!, label: typeof c.header === 'string' ? c.header : c.id!,
  }));
  const pagesCount = Math.max(1, Math.ceil(total / pageSize));

  const onRefresh = () => {
    if (ids.length === 0 || !region) return;
    setRefreshing(true);
    dispatch(searchPayments({ input: buildInput(ids, pageNumber, pageSize, filters), region }));
  };

  const empty = (
    <Box textAlign="center" padding={{ vertical: 'l' }}>
      <SpaceBetween size="s" alignItems="center">
        {error ? (
          <StatusIndicator type="error">Couldn&apos;t load payments</StatusIndicator>
        ) : (
          <Box color="text-body-secondary">No payments found.</Box>
        )}
        {error && <Button iconName="refresh" onClick={onRefresh}>Retry</Button>}
      </SpaceBetween>
    </Box>
  );

  return (
    <Table
      variant="container"
      resizableColumns
      sortingDisabled
      trackBy="id"
      loading={loading && !error}
      loadingText="Searching payments…"
      items={payments}
      columnDefinitions={activeColumns}
      header={
        <Header counter={`(${payments.length.toLocaleString()} of ${total.toLocaleString()})`}>
          Payments
        </Header>
      }
      filter={
        <PropertyFilter
          query={query}
          onChange={({ detail }) => { setFilterTokens(detail.tokens); setPageNumber(1); }}
          filteringProperties={paymentSearchDescriptor.filteringProperties}
          countText={`${query.tokens.length} filter${query.tokens.length === 1 ? '' : 's'}`}
          filteringPlaceholder="Filter payments by status, state"
          expandToViewport
        />
      }
      pagination={
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="icon"
            ariaLabel="Refresh payments"
            onClick={onRefresh}
            disabled={refreshing}
          />
          <Pagination
            currentPageIndex={pageNumber}
            pagesCount={pagesCount}
            onChange={({ detail }) => setPageNumber(detail.currentPageIndex)}
          />
        </SpaceBetween>
      }
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={{ pageSize, visibleContent: visibleColumns }}
          onConfirm={({ detail }) => {
            if (detail.pageSize && detail.pageSize !== pageSize) {
              setPageSize(detail.pageSize);
              setPageNumber(1);
            }
            if (detail.visibleContent) setVisibleColumns([...detail.visibleContent]);
          }}
          pageSizePreference={{ title: 'Page size', options: PAGE_SIZE_OPTIONS }}
          visibleContentPreference={{
            title: 'Visible columns',
            options: [{ label: `${paymentSearchDescriptor.title} columns`, options: visibleContentOptions }],
          }}
        />
      }
      empty={empty}
    />
  );
};
